import React from "react";
import Reveal from "./Reveal";
import Window from "./Window";
import { VscMarkdown } from "react-icons/vsc";

const facts = [
  { value: "1st", label: "class honours", tone: "bg-syntax-str/15 text-syntax-str border-syntax-str/30" },
  { value: "77%", label: "cumulative avg", tone: "bg-syntax-fn/15 text-syntax-fn border-syntax-fn/30" },
  { value: "3+", label: "years hands-on", tone: "bg-syntax-key/15 text-syntax-key border-syntax-key/30" },
  { value: "5+", label: "shipped projects", tone: "bg-syntax-num/15 text-syntax-num border-syntax-num/30" },
];

const Badge = ({ value, label, tone }) => (
  <div className="flex overflow-hidden rounded-md border border-editor-border text-xs">
    <span className="bg-editor-inset px-2.5 py-1 text-syntax-comment">{label}</span>
    <span className={`border-l px-2.5 py-1 font-bold ${tone}`}>{value}</span>
  </div>
);

const About = () => {
  return (
    <section name="about" className="relative w-full py-24 md:py-28">
      <div className="mx-auto max-w-4xl px-4">
        <Reveal className="mb-8">
          <p className="kicker">
            <span className="text-syntax-comment">01 /</span>
            <span className="text-syntax-fn">about</span>
          </p>
        </Reveal>

        <Reveal>
          <Window file="about.md" icon={<VscMarkdown className="text-syntax-fn" />}>
            <div className="grid gap-8 p-6 md:grid-cols-[1.5fr_1fr] md:p-8">
              {/* Rendered markdown */}
              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-white sm:text-3xl">
                  <span className="mr-2 text-syntax-tag">#</span>
                  About me
                </h2>
                <div className="mt-5 space-y-4 text-[0.95rem] leading-relaxed text-zinc-400">
                  <p>
                    I'm a software engineer on the graduate programme at{" "}
                    <span className="text-syntax-prop">JLR</span>, where four
                    rotations have taken me across embedded, web and full-stack
                    development. I hold a first-class degree in Computer Science
                    from the University of Salford, with a foundation in
                    object-oriented programming —{" "}
                    <span className="text-syntax-str">Java</span>,{" "}
                    <span className="text-syntax-str">C#</span> and{" "}
                    <span className="text-syntax-str">C++</span> — and a real
                    emphasis on code that's readable, testable and built to last.
                  </p>
                  <p>
                    Across those rotations I've built internal web apps with{" "}
                    <span className="text-syntax-str">React</span> and{" "}
                    <span className="text-syntax-str">Python</span>, written
                    embedded <span className="text-syntax-str">C</span> for ECUs
                    deployed in JLR vehicles, and automated engineering workflows
                    in Python and <span className="text-syntax-str">MATLAB</span>{" "}
                    for high-voltage inverter hardware — delivering through Agile
                    teams and CI/CD pipelines. Earlier, an IT-operations placement
                    at{" "}
                    <span className="text-syntax-prop">Egress Software Technologies</span>{" "}
                    gave me hands-on security-operations and{" "}
                    <span className="text-syntax-prop">Microsoft Azure</span>{" "}
                    experience.
                  </p>
                  <p>
                    I care about the whole journey — from a rough idea and a set
                    of requirements to a reliable, well-tested product. Outside of
                    work I keep sharpening the fundamentals, from algorithms to
                    new frameworks.
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {facts.map((f) => (
                    <Badge key={f.label} {...f} />
                  ))}
                </div>
              </div>

              {/* Meta panel — reads like frontmatter */}
              <div className="self-start rounded-lg border border-editor-border bg-editor-inset p-4 text-[0.85rem] leading-7">
                <p className="mb-2 text-syntax-comment">--- profile ---</p>
                {[
                  ["role", '"Software Engineer"', "text-syntax-str"],
                  ["company", '"JLR"', "text-syntax-str"],
                  ["degree", '"1st Class BSc"', "text-syntax-str"],
                  ["based", '"Coventry, UK"', "text-syntax-str"],
                  ["setup", '"hybrid"', "text-syntax-str"],
                ].map(([k, v, tone]) => (
                  <div key={k} className="flex gap-2 whitespace-pre">
                    <span className="text-syntax-prop">{k}</span>
                    <span className="text-syntax-comment">:</span>
                    <span className={tone}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Window>
        </Reveal>
      </div>
    </section>
  );
};

export default About;
